import type { JSX } from 'react';
import { createContext, useContext, useMemo, useState } from 'react';
import type { Nullable } from '~/utilities/type-guards';
import { isNull } from '~/utilities/type-guards';

export type SettingsToggleState = 'closed' | 'open';

export type NetworkConnectivityToggleState = 'closed' | 'open';

export type SidePanelToggleState =
  | 'attendee-list'
  | 'chat'
  | 'closed'
  | 'group-record-upload'
  | 'media';

type LiveLayoutContextType = {
  sidePanelState: SidePanelToggleState;
  setSidePanelState: (state: SidePanelToggleState) => void;
  stageDisplayState: StageDisplay;
  setStageDisplayState: (state: StageDisplay) => void;
  networkConnectivityDrawerState: NetworkConnectivityToggleState;
  setNetworkConnectivityDrawerState: (state: NetworkConnectivityToggleState) => void;
};

type MaximizedStageDisplay = {
  type: 'maximized';
  sidePanelPrevState: SidePanelToggleState;
};

type NormalStageDisplay = { type: 'normal' };

type StageDisplay = MaximizedStageDisplay | NormalStageDisplay;

const LiveLayoutContext = createContext<Nullable<LiveLayoutContextType>>(null);

/**
 * Function to build the Provider JSX with the context threaded through
 *
 * @param children - The inner JSX to inject into the DOM
 * @returns JSX.Element
 */
export const LiveLayoutProvider = ({
  children,
}: {
  children: Nullable<JSX.Element | JSX.Element[]>;
}): JSX.Element => {
  const [sidePanelState, setSidePanelState] = useState<SidePanelToggleState>('chat');
  const [stageDisplayState, setStageDisplayState] = useState<StageDisplay>({ type: 'normal' });
  const [networkConnectivityDrawerState, setNetworkConnectivityDrawerState] =
    useState<NetworkConnectivityToggleState>('closed');

  const context = useMemo(
    () => ({
      sidePanelState,
      setSidePanelState,
      stageDisplayState,
      setStageDisplayState,
      networkConnectivityDrawerState,
      setNetworkConnectivityDrawerState,
    }),
    [sidePanelState, stageDisplayState, networkConnectivityDrawerState],
  );

  return <LiveLayoutContext.Provider value={context}>{children}</LiveLayoutContext.Provider>;
};

/**
 * Hook that will check if the context exists before trying to use it so that
 * an error can be thrown if the context is trying to be called outside of the
 * provider
 *
 * @throws {Error} Will throw an error if trying to access the context outside the provider
 * @returns LiveLayoutContext
 */
export const useLiveLayoutContext = (): LiveLayoutContextType => {
  const context = useContext(LiveLayoutContext);

  if (isNull(context)) {
    throw new Error('Must use useLiveLayoutContext within LiveLayoutProvider');
  }

  return context;
};
